import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuthStore } from 'store/authStore';
import { useLoadingStore } from 'store/loadingStore';
import { verPruebas } from 'utils/verPruebas';

// Evita que múltiples requests 401/403 simultáneos disparen clearAuth varias veces
let handling401 = false;
let handling403 = false;

const API_URL = import.meta.env.SX_VITE_API_URL ?? 'http://api.app.test';
const APP_TOKEN = import.meta.env.SX_VITE_APP_TOKEN ?? '';

if (import.meta.env.DEV && !APP_TOKEN) {
  console.error('[client] SX_VITE_APP_TOKEN está vacío — todas las peticiones fallarán con 401. Reinicia el servidor Vite.');
}

const client = axios.create({
  baseURL: `${API_URL}/api/web`,
});

// Token de aplicación — doble seguro: defaults (para peticiones fuera del interceptor)
client.defaults.headers.common['Token'] = APP_TOKEN;

// Endpoints que NO deben disparar el PageLoader global.
// El heartbeat /auth/check corre en background (useSessionTimer); mostrar el
// overlay para él produce un flash visual periódico molesto al usuario.
const isSilentLoader = (url: string): boolean => url.includes('/auth/check');

// Rutas que reciben el interruptor "Obras de prueba" (ver el interceptor).
const CON_PRUEBAS = /^\/(dashboard\/|works\/(\d+$|kml\/export\/|export-xlsx\/|catalogs\/)|bot\/message\/)/;

// Attach headers en cada petición (belt+suspenders — cubre edge cases de Axios 1.x)
client.interceptors.request.use((config) => {
  if (!isSilentLoader(config.url ?? '')) {
    useLoadingStore.getState().show();
  }

  // Token de app — asignación directa (evita bugs de AxiosHeaders.set en 1.x)
  config.headers['Token'] = APP_TOKEN;

  // Interruptor "Obras de prueba" (2026-11-03): encendido, viaja como tests=1
  // (utils/verPruebas.ts) SOLO a lo que cuenta o lista obras: tableros, lista,
  // exportaciones, catálogos de filtros y el bot. A un guardado nunca.
  if (verPruebas() && (config.method ?? '').toLowerCase() === 'post' && CON_PRUEBAS.test(config.url ?? '')) {
    const d = config.data;
    if (d === undefined || d === null) config.data = { tests: 1 };
    else if (typeof d === 'object' && !Array.isArray(d) && !(d instanceof FormData) && !(d instanceof Blob)) config.data = { ...d, tests: 1 };
  }

  const raw = localStorage.getItem('sx-auth');
  if (raw) {
    try {
      const { state } = JSON.parse(raw);
      if (state?.token) config.headers['Authorization'] = `Bearer ${state.token}`;
    } catch {
      // ignore
    }
  }
  return config;
});

// El toast "Sesión expirada" no aplica para estos casos:
//   - /auth/login  → credenciales malas: Login.tsx ya muestra error inline.
//   - /auth/logout → cerrando voluntariamente: no es "expiración".
//   - usuario ya está en /login (sin sesión que invalidar).
//   - heartbeat /auth/check → el WebSocket force-logout es el canal autoritativo;
//     un check que carrera contra el logout no debe mostrar "expirada".
const isSilent401 = (url: string, pathname: string): boolean =>
  url.includes('/auth/login')
  || url.includes('/auth/logout')
  || url.includes('/auth/check')
  || pathname.startsWith('/login');

// Desactivar spinner global — tanto en éxito como en error
client.interceptors.response.use(
  (r) => {
    if (!isSilentLoader(r.config?.url ?? '')) {
      useLoadingStore.getState().hide();
    }
    return r;
  },
  (err) => {
    if (!isSilentLoader(err.config?.url ?? '')) {
      useLoadingStore.getState().hide();
    }
    if (err.response?.status === 401 && !handling401) {
      const url      = err.config?.url ?? '';
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (isSilent401(url, pathname)) {
        console.warn('[401] Silencioso (auth endpoint o ya en /login). URL:', url);
        return Promise.reject(err);
      }
      handling401 = true;
      console.warn('[401] Sesión no válida. URL:', url, '| Response:', err.response?.data);
      toast.error('Sesión expirada. Inicia sesión nuevamente.', { toastId: 'session-expired' });
      setTimeout(() => {
        useAuthStore.getState().clearAuth();
        handling401 = false;
      }, 2000);
    }
    if (err.response?.status === 403 && !handling403) {
      handling403 = true;
      console.warn('[403] Sin permiso. URL:', err.config?.url);
      // 403 = falta de permiso, NO sesión inválida — no limpiar auth ni redirigir
      setTimeout(() => { handling403 = false; }, 3000);
    }
    return Promise.reject(err);
  }
);

export default client;
